import {
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  approveExternalAudit,
  createExternalAudit,
  deleteExternalAudit,
  deleteExternalAuditAttachment,
  downloadExternalAuditAttachment,
  downloadExternalAuditPdf,
  fetchExternalAudit,
  fetchExternalAudits,
  revokeExternalAudit,
  updateExternalAudit,
  uploadExternalAuditAttachments,
} from "../../api/externalAudit";
import {
  issueExternalAuditGuest,
  listGuests,
  revokeGuest,
} from "../../api/guestAccess";
import { fetchCompanyPlanStatus } from "../../api/companyPlan";
import { useCompanyStore } from "../../store/companyStore";
import { APPROVAL_TARGET_EXTERNAL_AUDIT } from "../../types/approval";
import { canConfirmInspection, isGuest } from "../../types/companyRole";
import { GuestAccess, GuestIssueResponse } from "../../types/guestAccess";
import {
  auditTypeLabel,
  createEmptyAuditor,
  createEmptyFinding,
  defaultAuditors,
  defaultFindings,
  EXTERNAL_AUDIT_FINDING_STATUSES,
  EXTERNAL_AUDIT_FINDING_TYPES,
  EXTERNAL_AUDIT_TYPES,
  ExternalAuditAttachment,
  ExternalAuditAuditor,
  ExternalAuditFinding,
  ExternalAuditReport,
  parseAuditors,
  parseFindings,
  summarizeAuditors,
  summarizeFindings,
} from "../../types/externalAudit";
import { notifyError, reportError } from "../../utils/notify";
import {
  ApprovalPendingFields,
  isApprovalInProgress,
  withApprovalPendingClass,
  withApprovalPendingLabel,
} from "../../utils/approvalPending";
import ApprovalCancelButton from "../approval/ApprovalCancelButton";
import ApprovalRequestModal from "../approval/ApprovalRequestModal";

import "./EducationAnnualPlanTab.css";
import "./ExternalAuditPanel.css";
import "./QualityShell.css";
import "../product/InspectionFormSheet.css";
import "../product/NonconformanceReportPanel.css";

function formatDate(value?: string | null) {
  if (!value) return "-";
  return value.slice(0, 10);
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDaysIso(baseIso: string, days: number) {
  const d = new Date(`${baseIso}T00:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function defaultGuestExpires(auditDate?: string | null) {
  const today = todayIso();
  const base =
    auditDate && auditDate.slice(0, 10) >= today
      ? auditDate.slice(0, 10)
      : today;
  return addDaysIso(base, 14);
}

function formatSize(size?: number | null) {
  if (size == null) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function apiErrorMessage(err: unknown, fallback: string) {
  const message = (err as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return message?.trim() || fallback;
}

function isApproved(status?: string | null) {
  return (status || "").toUpperCase() === "APPROVED";
}

function isDraft(status?: string | null) {
  const s = (status || "DRAFT").toUpperCase();
  return s === "DRAFT" || s === "";
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ["작성", authorName],
          ["검토", reviewerName],
          ["승인", approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span
            style={{
              color: value ? "#1f2a37" : "#98a2b3",
              fontWeight: value ? 600 : 400,
            }}
          >
            {value || "-"}
          </span>
        </div>
      ))}
    </div>
  );
}

function FindingSummaryChips({
  findings,
}: {
  findings: ExternalAuditFinding[];
}) {
  const s = summarizeFindings(findings);
  if (s.total === 0) {
    return <span className="ea-chip ea-chip--muted">지적 없음</span>;
  }
  return (
    <div className="ea-chips">
      <span className="ea-chip">전체 {s.total}</span>
      {s.nc > 0 ? <span className="ea-chip ea-chip--nc">NC {s.nc}</span> : null}
      {s.ofi > 0 ? (
        <span className="ea-chip ea-chip--ofi">OFI {s.ofi}</span>
      ) : null}
      {s.obs > 0 ? (
        <span className="ea-chip ea-chip--obs">OBS {s.obs}</span>
      ) : null}
      {s.open > 0 ? (
        <span className="ea-chip ea-chip--open">미결 {s.open}</span>
      ) : null}
    </div>
  );
}

type DocView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; reportId: number }
  | { type: "detail"; reportId: number };

const LIST_VIEW: DocView = { type: "list" };

function ExternalAuditPanel({
  contractId = null,
  embedded = false,
}: {
  contractId?: number | null;
  embedded?: boolean;
} = {}) {
  void embedded;
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const setPlan = useCompanyStore((s) => s.setPlan);
  const canApproveDirect = canConfirmInspection(role);
  const guestMode = isGuest(role);
  const [guestIssuanceEnabled, setGuestIssuanceEnabled] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const [view, setView] = useState<DocView>(LIST_VIEW);
  const [rows, setRows] = useState<ExternalAuditReport[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [error, setError] = useState("");
  const [approvalMessage, setApprovalMessage] = useState("");
  const [approvalOpen, setApprovalOpen] = useState(false);

  const [guestIssueOpen, setGuestIssueOpen] = useState(false);
  const [guestIssueEmail, setGuestIssueEmail] = useState("");
  const [guestIssueName, setGuestIssueName] = useState("");
  const [guestIssueExpires, setGuestIssueExpires] = useState(todayIso());
  const [guestIssuing, setGuestIssuing] = useState(false);
  const [guestIssueResult, setGuestIssueResult] =
    useState<GuestIssueResponse | null>(null);

  const [guestManageOpen, setGuestManageOpen] = useState(false);
  const [guestList, setGuestList] = useState<GuestAccess[]>([]);
  const [guestListLoading, setGuestListLoading] = useState(false);
  const [guestRevokingId, setGuestRevokingId] = useState<number | null>(null);

  const [reportNo, setReportNo] = useState("");
  const [title, setTitle] = useState("");
  const [auditDate, setAuditDate] = useState(todayIso());
  const [auditType, setAuditType] = useState("CERTIFICATION");
  const [auditOrg, setAuditOrg] = useState("");
  const [location, setLocation] = useState("");
  const [scopeText, setScopeText] = useState("");
  const [auditors, setAuditors] =
    useState<ExternalAuditAuditor[]>(defaultAuditors());
  const [findings, setFindings] =
    useState<ExternalAuditFinding[]>(defaultFindings());
  const [conclusion, setConclusion] = useState("");
  const [followUpDue, setFollowUpDue] = useState("");
  const [remark, setRemark] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [authorName, setAuthorName] = useState<string | null>(null);
  const [reviewerName, setReviewerName] = useState<string | null>(null);
  const [approverName, setApproverName] = useState<string | null>(null);
  const [canRevoke, setCanRevoke] = useState(false);
  const [approvalPending, setApprovalPending] = useState<ApprovalPendingFields>(
    {},
  );
  const [attachments, setAttachments] = useState<ExternalAuditAttachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const editId =
    view.type === "edit" || view.type === "detail" ? view.reportId : null;
  const showForm = view.type !== "list";
  const isDetail = view.type === "detail";
  const isEditing = view.type === "create" || view.type === "edit";
  const locked = !isEditing;

  const attachmentsLocked =
    isApproved(status) ||
    isApprovalInProgress(approvalPending) ||
    (!isEditing && !isDraft(status));

  const applyDetail = (detail: ExternalAuditReport) => {
    setReportNo(detail.reportNo || "");
    setTitle(detail.title || "");
    setAuditDate(detail.auditDate ? detail.auditDate.slice(0, 10) : todayIso());
    setAuditType((detail.auditType || "CERTIFICATION").toUpperCase());
    setAuditOrg(detail.auditOrg || "");
    setLocation(detail.location || "");
    setScopeText(detail.scopeText || "");
    setAuditors(parseAuditors(detail.auditorsJson));
    setFindings(parseFindings(detail.findingsJson));
    setConclusion(detail.conclusion || "");
    setFollowUpDue(detail.followUpDue ? detail.followUpDue.slice(0, 10) : "");
    setRemark(detail.remark || "");
    setStatus(detail.status || "DRAFT");
    setAuthorName(detail.authorName ?? null);
    setReviewerName(detail.reviewerName ?? null);
    setApproverName(detail.approverName ?? null);
    setCanRevoke(detail.canRevoke ?? false);
    setApprovalPending({
      approvalInProgress: detail.approvalInProgress,
      pendingApprovalRequestId: detail.pendingApprovalRequestId,
      canCancelApproval: detail.canCancelApproval,
    });
    setAttachments(detail.attachments || []);
  };

  const loadList = useCallback(
    async (keyword?: string) => {
      try {
        setLoading(true);
        setRows(await fetchExternalAudits(keyword, contractId));
        setError("");
      } catch (err: unknown) {
        reportError(
          setError,
          apiErrorMessage(err, "외부심사 목록을 불러오지 못했습니다."),
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [contractId],
  );

  useEffect(() => {
    setView(LIST_VIEW);
    setSearchKeyword("");
    void loadList();
  }, [companyId, loadList, setView]);

  useEffect(() => {
    let cancelled = false;
    void fetchCompanyPlanStatus()
      .then((status) => {
        if (cancelled) return;
        setGuestIssuanceEnabled(Boolean(status.guestIssuanceEnabled));
        setPlan(status.plan);
      })
      .catch(() => {
        if (!cancelled) setGuestIssuanceEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, setPlan]);

  useEffect(() => {
    if (showForm) return;
    const timer = window.setTimeout(() => void loadList(searchKeyword), 250);
    return () => window.clearTimeout(timer);
  }, [searchKeyword, loadList, showForm]);

  const resetForm = () => {
    setReportNo("");
    setTitle("");
    setAuditDate(todayIso());
    setAuditType("CERTIFICATION");
    setAuditOrg("");
    setLocation("");
    setScopeText("");
    setAuditors(defaultAuditors());
    setFindings(defaultFindings());
    setConclusion("");
    setFollowUpDue("");
    setRemark("");
    setStatus("DRAFT");
    setAuthorName(null);
    setReviewerName(null);
    setApproverName(null);
    setCanRevoke(false);
    setApprovalPending({});
    setAttachments([]);
    setPendingFiles([]);
    setApprovalMessage("");
    setApprovalOpen(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const openCreate = () => {
    resetForm();
    setView({ type: "create" });
  };

  const openDetail = async (row: ExternalAuditReport) => {
    try {
      setLoading(true);
      setError("");
      const detail = await fetchExternalAudit(row.reportId);
      applyDetail(detail);
      setPendingFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      setView({ type: "detail", reportId: row.reportId });
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "외부심사를 불러오지 못했습니다."),
      );
    } finally {
      setLoading(false);
    }
  };

  const openEditFromDetail = () => {
    if (editId == null || isApproved(status)) return;
    setView({ type: "edit", reportId: editId });
  };

  const backToList = () => {
    resetForm();
    setView(LIST_VIEW);
    void loadList(searchKeyword);
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (locked) {
      reportError(setError, "승인된 외부심사는 수정할 수 없습니다.");
      return;
    }
    if (!title.trim()) {
      reportError(setError, "제목을 입력해 주세요.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        title: title.trim(),
        auditDate: auditDate || null,
        auditType: auditType || null,
        auditOrg: auditOrg.trim() || null,
        location: location.trim() || null,
        scopeText: scopeText.trim() || null,
        auditorsJson: JSON.stringify(auditors),
        findingsJson: JSON.stringify(findings),
        conclusion: conclusion.trim() || null,
        followUpDue: followUpDue || null,
        remark: remark.trim() || null,
        contractId: contractId ?? undefined,
      };
      let reportId = editId;
      let saved: ExternalAuditReport;
      if (reportId != null) {
        saved = await updateExternalAudit(reportId, payload);
      } else {
        saved = await createExternalAudit(payload);
        reportId = saved.reportId;
      }
      if (pendingFiles.length > 0 && reportId != null) {
        const uploaded = await uploadExternalAuditAttachments(
          reportId,
          pendingFiles,
        );
        saved = {
          ...saved,
          attachments: [...(saved.attachments || []), ...uploaded],
          attachmentCount: (saved.attachmentCount ?? 0) + uploaded.length,
        };
        setPendingFiles([]);
      }
      applyDetail(saved);
      setPendingFiles([]);
      setView({ type: "detail", reportId: saved.reportId });
      await loadList(searchKeyword);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "외부심사 저장에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFromList = async (
    row: ExternalAuditReport,
    event: MouseEvent,
  ) => {
    event.stopPropagation();
    if (isApproved(row.status)) {
      reportError(setError, "승인된 외부심사는 삭제할 수 없습니다.");
      return;
    }
    if (!window.confirm(`"${row.title}"를 삭제할까요?`)) return;
    try {
      await deleteExternalAudit(row.reportId);
      await loadList(searchKeyword);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "외부심사 삭제에 실패했습니다."),
      );
    }
  };

  const handleApprove = async () => {
    if (editId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "외부심사를 바로 승인할까요? 승인 후에는 본문을 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError("");
      const saved = await approveExternalAudit(editId);
      applyDetail(saved);
      setApprovalMessage("바로 승인되었습니다.");
      await loadList(searchKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (editId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 초기화됩니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeExternalAudit(editId);
      applyDetail(saved);
      setApprovalMessage("승인이 철회되었습니다.");
      await loadList(searchKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (editId == null) return;
    try {
      setPdfDownloading(true);
      const name = reportNo?.trim() || title?.trim() || String(editId);
      await downloadExternalAuditPdf(editId, `외부심사보고서_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const openGuestIssue = (auditor?: ExternalAuditAuditor) => {
    if (!guestIssuanceEnabled) {
      notifyError("게스트 계정 발급은 프로 요금제에서만 가능합니다.");
      return;
    }
    setGuestIssueEmail(auditor?.email?.trim() || "");
    setGuestIssueName(auditor?.name?.trim() || "");
    setGuestIssueExpires(defaultGuestExpires(auditDate));
    setGuestIssueResult(null);
    setGuestIssueOpen(true);
  };

  const submitGuestIssue = async () => {
    if (editId == null) return;
    if (!guestIssueEmail.trim() || !guestIssueEmail.includes("@")) {
      notifyError("이메일을 입력해 주세요.");
      return;
    }
    if (!guestIssueExpires) {
      notifyError("만료일을 입력해 주세요.");
      return;
    }
    try {
      setGuestIssuing(true);
      const result = await issueExternalAuditGuest(editId, {
        email: guestIssueEmail.trim(),
        name: guestIssueName.trim() || undefined,
        expiresAt: guestIssueExpires,
      });
      setGuestIssueResult(result);
    } catch (err: unknown) {
      notifyError(apiErrorMessage(err, "게스트 발급에 실패했습니다."));
    } finally {
      setGuestIssuing(false);
    }
  };

  const openGuestManage = async () => {
    setGuestManageOpen(true);
    try {
      setGuestListLoading(true);
      setGuestList(await listGuests());
    } catch (err: unknown) {
      notifyError(apiErrorMessage(err, "게스트 목록을 불러오지 못했습니다."));
      setGuestList([]);
    } finally {
      setGuestListLoading(false);
    }
  };

  const handleRevokeGuest = async (guestAccessId: number) => {
    if (
      !window.confirm(
        "이 게스트 접근을 철회할까요?\n관련 DB 데이터(계정·역할·이력)도 삭제됩니다.",
      )
    )
      return;
    try {
      setGuestRevokingId(guestAccessId);
      await revokeGuest(guestAccessId);
      setGuestList((prev) =>
        prev.map((g) =>
          g.guestAccessId === guestAccessId
            ? { ...g, status: "REVOKED", expired: true }
            : g,
        ),
      );
    } catch (err: unknown) {
      notifyError(apiErrorMessage(err, "게스트 철회에 실패했습니다."));
    } finally {
      setGuestRevokingId(null);
    }
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      notifyError("클립보드 복사에 실패했습니다.");
    }
  };

  const renderGuestModals = () => {
    if (typeof document === "undefined") return null;
    return createPortal(
      <>
        {guestIssueOpen ? (
          <div
            className="ea-modal-backdrop"
            role="dialog"
            aria-modal="true"
            data-guest-admin-ui="true"
            onClick={() => !guestIssuing && setGuestIssueOpen(false)}
          >
            <div className="ea-modal" onClick={(e) => e.stopPropagation()}>
              <h3>심사원 게스트 발급</h3>
              <p className="ea-modal__hint">
                임시 아이디·비밀번호가 한 번만 표시됩니다. 안전한 경로로
                전달하세요. 심사원은 로그인 후 회사 문서를 읽기 전용으로
                열람합니다. (로그인 식별자: 이메일)
              </p>
              {!guestIssueResult ? (
                <div className="ea-modal__fields">
                  <label>
                    이메일
                    <input
                      type="email"
                      value={guestIssueEmail}
                      onChange={(e) => setGuestIssueEmail(e.target.value)}
                    />
                  </label>
                  <label>
                    이름
                    <input
                      value={guestIssueName}
                      onChange={(e) => setGuestIssueName(e.target.value)}
                    />
                  </label>
                  <label>
                    만료일
                    <input
                      type="date"
                      value={guestIssueExpires}
                      onChange={(e) => setGuestIssueExpires(e.target.value)}
                    />
                  </label>
                  <div className="ea-modal__actions">
                    <button
                      type="button"
                      className="ncr-btn is-secondary"
                      disabled={guestIssuing}
                      onClick={() => setGuestIssueOpen(false)}
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      className="ncr-btn is-primary"
                      disabled={guestIssuing}
                      onClick={() => void submitGuestIssue()}
                    >
                      {guestIssuing ? "발급 중…" : "발급"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ea-modal__fields">
                  <p className="ea-modal__warn">
                    임시 비밀번호는 이 화면에서만 확인 가능합니다.
                  </p>
                  <div>
                    <strong>로그인 ID</strong>
                    <div className="ea-modal__copy-row">
                      <code>{guestIssueResult.username}</code>
                      <button
                        type="button"
                        className="ncr-btn is-secondary"
                        onClick={() => void copyText(guestIssueResult.username)}
                      >
                        복사
                      </button>
                    </div>
                  </div>
                  {guestIssueResult.temporaryPassword ? (
                    <div>
                      <strong>임시 비밀번호</strong>
                      <div className="ea-modal__copy-row">
                        <code>{guestIssueResult.temporaryPassword}</code>
                        <button
                          type="button"
                          className="ncr-btn is-secondary"
                          onClick={() =>
                            void copyText(
                              guestIssueResult.temporaryPassword || "",
                            )
                          }
                        >
                          복사
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="ea-modal__hint">
                      기존 계정 재사용 · 비밀번호는 변경되지 않았습니다.
                    </p>
                  )}
                  <p className="ea-modal__hint">
                    만료: {formatDate(guestIssueResult.expiresAt)}
                  </p>
                  <div className="ea-modal__actions">
                    <button
                      type="button"
                      className="ncr-btn is-primary"
                      onClick={() => setGuestIssueOpen(false)}
                    >
                      확인
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {guestManageOpen ? (
          <div
            className="ea-modal-backdrop"
            role="dialog"
            aria-modal="true"
            data-guest-admin-ui="true"
            onClick={() => setGuestManageOpen(false)}
          >
            <div
              className="ea-modal ea-modal--wide"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ea-modal__head">
                <div>
                  <h3>게스트 관리</h3>
                  <p className="ea-modal__hint">
                    회사 게스트 접근 목록 · 철회·만료 시 DB에서도 삭제됩니다.
                  </p>
                </div>
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  onClick={() => setGuestManageOpen(false)}
                >
                  닫기
                </button>
              </div>
              <div className="quality-table-wrap">
                <table className="quality-table">
                  <thead>
                    <tr>
                      <th>사용자</th>
                      <th>이메일</th>
                      <th>만료</th>
                      <th>상태</th>
                      <th style={{ width: 88 }}>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guestListLoading ? (
                      <tr>
                        <td colSpan={5}>불러오는 중…</td>
                      </tr>
                    ) : guestList.length === 0 ? (
                      <tr>
                        <td colSpan={5}>발급된 게스트가 없습니다.</td>
                      </tr>
                    ) : (
                      guestList.map((g) => (
                        <tr key={g.guestAccessId}>
                          <td>
                            {g.userLabel || g.name || g.username || g.userId}
                          </td>
                          <td>{g.email || "-"}</td>
                          <td>{formatDate(g.expiresAt)}</td>
                          <td>
                            {g.status}
                            {g.expired ? " · 만료" : ""}
                          </td>
                          <td>
                            {g.status === "ACTIVE" && !g.expired ? (
                              <button
                                type="button"
                                className="ncr-btn is-danger"
                                disabled={guestRevokingId === g.guestAccessId}
                                onClick={() =>
                                  void handleRevokeGuest(g.guestAccessId)
                                }
                              >
                                {guestRevokingId === g.guestAccessId
                                  ? "철회 중…"
                                  : "철회"}
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}
      </>,
      document.body,
    );
  };

  const handleUploadNow = async (files: FileList | null) => {
    if (!files?.length || attachmentsLocked) return;
    const selected = Array.from(files);
    if (fileRef.current) fileRef.current.value = "";
    if (editId == null) {
      setPendingFiles((prev) => [...prev, ...selected]);
      return;
    }
    try {
      setSaving(true);
      const uploaded = await uploadExternalAuditAttachments(editId, selected);
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 업로드에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttachment = async (
    attachment: ExternalAuditAttachment,
  ) => {
    if (editId == null || attachmentsLocked) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    try {
      setSaving(true);
      await deleteExternalAuditAttachment(editId, attachment.attachmentId);
      setAttachments((prev) =>
        prev.filter((a) => a.attachmentId !== attachment.attachmentId),
      );
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 삭제에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const patchAuditor = (
    id: string,
    key: keyof ExternalAuditAuditor,
    value: string,
  ) => {
    setAuditors((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)),
    );
  };

  const patchFinding = (
    id: string,
    key: keyof ExternalAuditFinding,
    value: string,
  ) => {
    setFindings((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)),
    );
  };

  const renderDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void handleDownloadPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {isDetail &&
      canApproveDirect &&
      editId != null &&
      !guestMode &&
      guestIssuanceEnabled ? (
        <button
          type="button"
          className="ncr-btn is-secondary"
          onClick={() => {
            const withEmail = auditors.find((a) => a.email.trim());
            openGuestIssue(withEmail);
          }}
        >
          심사원 게스트 발급
        </button>
      ) : null}
      {isDetail &&
      canApproveDirect &&
      !guestMode &&
      !guestIssuanceEnabled ? (
        <span
          className="quality-form__hint"
          style={{ fontSize: 12, alignSelf: "center" }}
        >
          게스트 발급은 프로 요금제에서 가능합니다
        </span>
      ) : null}
      {canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={saving || approving}
          onClick={() => void handleRevoke()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {isDraft(status) &&
      !guestMode &&
      !isApprovalInProgress(approvalPending) ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={openEditFromDetail}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={saving || approving}
            onClick={() => {
              setApprovalMessage("");
              setApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={saving || approving}
              onClick={() => void handleApprove()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={saving || approving}
            onClick={() => {
              if (editId == null) return;
              if (!window.confirm(`"${title}"를 삭제할까요?`)) return;
              void deleteExternalAudit(editId).then(() => {
                backToList();
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      {isApprovalInProgress(approvalPending) && !guestMode ? (
        <ApprovalCancelButton
          detail={approvalPending}
          className="ncr-btn is-ghost"
          disabled={saving || approving}
          onCancelled={async () => {
            if (editId == null) return;
            const detail = await fetchExternalAudit(editId);
            applyDetail(detail);
            void loadList(searchKeyword);
          }}
        />
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        disabled={saving || approving}
        onClick={backToList}
      >
        목록
      </button>
    </div>
  );

  const renderEditActions = () => (
    <div className="quality-form__actions">
      <button
        type="submit"
        className="ncr-btn is-primary"
        disabled={saving || approving}
      >
        {saving ? "저장 중..." : "저장"}
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        disabled={saving || approving}
        onClick={() =>
          view.type === "edit" && editId != null
            ? setView({ type: "detail", reportId: editId })
            : backToList()
        }
      >
        {view.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  if (showForm) {
    return (
      <main className="quality-shell sg-panel">
        <form
          className="quality-form"
          onSubmit={
            isEditing ? (e) => void handleSave(e) : (e) => e.preventDefault()
          }
        >
          {isDetail ? (
            <div
              className="quality-shell__toolbar"
              style={{ marginBottom: 12 }}
            >
              <div>
                <h3 style={{ margin: 0 }}>외부심사 상세</h3>
                <p className="quality-form__hint" style={{ margin: "4px 0 0" }}>
                  {reportNo || "-"} · {title || "-"} ·{" "}
                  {withApprovalPendingLabel(
                    isApproved(status) ? "승인" : "작성중",
                    approvalPending.approvalInProgress,
                  )}
                </p>
              </div>
              {renderDetailActions()}
            </div>
          ) : (
            renderEditActions()
          )}

          {error ? <p className="quality-error">{error}</p> : null}
          {approvalMessage ? (
            <p className="quality-form__hint">{approvalMessage}</p>
          ) : null}

          <section className="quality-form__section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                alignItems: "flex-start",
                marginBottom: 12,
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>
                  {view.type === "create"
                    ? "외부심사 등록"
                    : view.type === "edit"
                      ? "외부심사 수정"
                      : "외부심사"}
                </h3>
                {isEditing ? (
                  <p
                    className="quality-form__hint"
                    style={{ margin: "4px 0 0" }}
                  >
                    저장하면 상세보기로 이동합니다.
                  </p>
                ) : null}
              </div>
              <StampBox
                authorName={authorName}
                reviewerName={reviewerName}
                approverName={approverName}
              />
            </div>
            <div className="quality-form__grid">
              <label>
                문서번호
                <input
                  value={
                    reportNo ||
                    (view.type === "create" ? "저장 시 자동 채번" : "-")
                  }
                  readOnly
                />
              </label>
              <label>
                심사일
                <input
                  type="date"
                  value={auditDate}
                  onChange={(e) => setAuditDate(e.target.value)}
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                제목 *
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="외부심사 제목"
                  maxLength={255}
                  required
                  readOnly={locked}
                />
              </label>
              <label>
                심사유형
                <select
                  value={auditType}
                  onChange={(e) => setAuditType(e.target.value)}
                  disabled={locked}
                >
                  {EXTERNAL_AUDIT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                인증기관/고객사
                <input
                  value={auditOrg}
                  onChange={(e) => setAuditOrg(e.target.value)}
                  placeholder="심사 기관명"
                  maxLength={255}
                  readOnly={locked}
                />
              </label>
              <label>
                장소
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="심사 장소"
                  maxLength={255}
                  readOnly={locked}
                />
              </label>
              <label>
                후속기한
                <input
                  type="date"
                  value={followUpDue}
                  onChange={(e) => setFollowUpDue(e.target.value)}
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                심사범위
                <textarea
                  rows={3}
                  value={scopeText}
                  onChange={(e) => setScopeText(e.target.value)}
                  placeholder="심사 범위"
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                결론
                <textarea
                  rows={4}
                  value={conclusion}
                  onChange={(e) => setConclusion(e.target.value)}
                  placeholder="심사 결론"
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                비고
                <textarea
                  rows={3}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="메모"
                  maxLength={2000}
                  readOnly={locked}
                />
              </label>
            </div>
          </section>

          <section className="quality-form__section">
            <div className="ea-section-head">
              <div>
                <h3 style={{ margin: 0 }}>심사관</h3>
                <p className="quality-form__hint" style={{ margin: "4px 0 0" }}>
                  외부 심사관 성명·소속·휴대폰·이메일을 기록합니다. 게스트
                  계정은 프로 요금제에서 문서 상세의 「게스트 발급」또는 상단
                  「심사원 게스트 발급」으로 만듭니다. (이메일 필수 · 팀장/오너
                  권한)
                </p>
              </div>
              <div className="ea-section-head__right">
                <span className="ea-chip ea-chip--muted">
                  {summarizeAuditors(auditors)}명
                </span>
                {!locked ? (
                  <button
                    type="button"
                    className="ncr-btn is-secondary"
                    onClick={() =>
                      setAuditors((prev) => [...prev, createEmptyAuditor()])
                    }
                  >
                    심사관 추가
                  </button>
                ) : null}
              </div>
            </div>
            <div className="ea-auditor-list">
              {auditors.map((row, index) => (
                <div key={row.id} className="ea-auditor-card">
                  <div className="ea-auditor-card__title">
                    심사관 {index + 1}
                  </div>
                  <div className="ea-auditor-card__grid">
                    <label>
                      성명
                      <input
                        value={row.name}
                        onChange={(e) =>
                          patchAuditor(row.id, "name", e.target.value)
                        }
                        readOnly={locked}
                        placeholder="성명"
                      />
                    </label>
                    <label>
                      소속
                      <input
                        value={row.org}
                        onChange={(e) =>
                          patchAuditor(row.id, "org", e.target.value)
                        }
                        readOnly={locked}
                        placeholder="소속"
                      />
                    </label>
                    <label>
                      역할
                      <input
                        value={row.role}
                        onChange={(e) =>
                          patchAuditor(row.id, "role", e.target.value)
                        }
                        readOnly={locked}
                        placeholder="수석심사원 등"
                      />
                    </label>
                    <label>
                      휴대폰
                      <input
                        type="tel"
                        value={row.phone}
                        onChange={(e) =>
                          patchAuditor(row.id, "phone", e.target.value)
                        }
                        readOnly={locked}
                        placeholder="010-0000-0000"
                      />
                    </label>
                    <label className="is-span-2">
                      이메일
                      <input
                        type="email"
                        value={row.email}
                        onChange={(e) =>
                          patchAuditor(row.id, "email", e.target.value)
                        }
                        readOnly={locked}
                        placeholder="guest@example.com"
                      />
                    </label>
                  </div>
                  <div className="ea-auditor-card__actions">
                    {!locked ? (
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        onClick={() =>
                          setAuditors((prev) => {
                            const next = prev.filter((r) => r.id !== row.id);
                            return next.length ? next : [createEmptyAuditor()];
                          })
                        }
                      >
                        삭제
                      </button>
                    ) : isDetail &&
                      canApproveDirect &&
                      guestIssuanceEnabled &&
                      row.email.trim() ? (
                      <button
                        type="button"
                        className="ncr-btn is-secondary"
                        onClick={() => openGuestIssue(row)}
                      >
                        게스트 발급
                      </button>
                    ) : isDetail && canApproveDirect && guestIssuanceEnabled ? (
                      <span
                        className="quality-form__hint"
                        style={{ fontSize: 11 }}
                      >
                        게스트 발급에는 이메일이 필요합니다
                      </span>
                    ) : isDetail && canApproveDirect ? (
                      <span
                        className="quality-form__hint"
                        style={{ fontSize: 11 }}
                      >
                        게스트 발급은 프로 요금제
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="quality-form__section">
            <div className="ea-section-head">
              <div>
                <h3 style={{ margin: 0 }}>지적 · 시정조치</h3>
                <p className="quality-form__hint" style={{ margin: "4px 0 0" }}>
                  NC / OFI / OBS를 기록하고 시정 기한·종결 여부를 관리합니다.
                </p>
              </div>
              <div className="ea-section-head__right">
                <FindingSummaryChips findings={findings} />
                {!locked ? (
                  <button
                    type="button"
                    className="ncr-btn is-secondary"
                    onClick={() =>
                      setFindings((prev) => [...prev, createEmptyFinding()])
                    }
                  >
                    지적 추가
                  </button>
                ) : null}
              </div>
            </div>
            <div className="quality-table-wrap">
              <table className="quality-table">
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>유형</th>
                    <th style={{ width: 100 }}>조항</th>
                    <th>지적사항</th>
                    <th>시정조치</th>
                    <th style={{ width: 120 }}>기한</th>
                    <th style={{ width: 88 }}>상태</th>
                    {!locked ? <th style={{ width: 72 }}>관리</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {findings.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <select
                          value={row.findingType}
                          onChange={(e) =>
                            patchFinding(row.id, "findingType", e.target.value)
                          }
                          disabled={locked}
                        >
                          {EXTERNAL_AUDIT_FINDING_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <textarea
                          rows={2}
                          value={row.clause}
                          onChange={(e) =>
                            patchFinding(row.id, "clause", e.target.value)
                          }
                          readOnly={locked}
                          placeholder="조항"
                        />
                      </td>
                      <td>
                        <textarea
                          rows={2}
                          value={row.finding}
                          onChange={(e) =>
                            patchFinding(row.id, "finding", e.target.value)
                          }
                          readOnly={locked}
                          placeholder="지적사항"
                        />
                      </td>
                      <td>
                        <textarea
                          rows={2}
                          value={row.correctiveAction}
                          onChange={(e) =>
                            patchFinding(
                              row.id,
                              "correctiveAction",
                              e.target.value,
                            )
                          }
                          readOnly={locked}
                          placeholder="시정조치"
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          value={row.dueDate}
                          onChange={(e) =>
                            patchFinding(row.id, "dueDate", e.target.value)
                          }
                          readOnly={locked}
                        />
                      </td>
                      <td>
                        <select
                          value={row.status}
                          onChange={(e) =>
                            patchFinding(row.id, "status", e.target.value)
                          }
                          disabled={locked}
                        >
                          {EXTERNAL_AUDIT_FINDING_STATUSES.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      {!locked ? (
                        <td>
                          <button
                            type="button"
                            className="ncr-btn is-danger"
                            onClick={() =>
                              setFindings((prev) => {
                                const next = prev.filter(
                                  (r) => r.id !== row.id,
                                );
                                return next.length
                                  ? next
                                  : [createEmptyFinding()];
                              })
                            }
                          >
                            삭제
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="quality-form__section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <h3 style={{ margin: 0 }}>첨부파일</h3>
              {!attachmentsLocked ? (
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  disabled={saving || approving}
                  onClick={() => fileRef.current?.click()}
                >
                  파일 추가
                </button>
              ) : null}
              <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                onChange={(e) => void handleUploadNow(e.target.files)}
              />
            </div>
            <p className="quality-form__hint">
              관련 파일을 첨부할 수 있습니다. (최대 20개, 각 20MB)
              {editId == null ? " 신규는 저장 시 함께 업로드됩니다." : ""}
            </p>
            <ul className="edu-plan-files">
              {attachments.map((file) => (
                <li key={file.attachmentId}>
                  <div>
                    <strong>{file.fileName}</strong>
                    <span>{formatSize(file.fileSize)}</span>
                  </div>
                  <div className="edu-plan-files__actions">
                    <button
                      type="button"
                      className="ncr-btn is-secondary"
                      onClick={() =>
                        void downloadExternalAuditAttachment(
                          editId!,
                          file.attachmentId,
                          file.fileName,
                        ).catch(() =>
                          reportError(
                            setError,
                            "첨부파일 다운로드에 실패했습니다.",
                          ),
                        )
                      }
                    >
                      다운로드
                    </button>
                    {!attachmentsLocked ? (
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        disabled={saving || approving}
                        onClick={() => void handleDeleteAttachment(file)}
                      >
                        삭제
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
              {pendingFiles.map((file, idx) => (
                <li key={`pending-${file.name}-${idx}`}>
                  <div>
                    <strong>{file.name}</strong>
                    <span>저장 대기 · {formatSize(file.size)}</span>
                  </div>
                  <button
                    type="button"
                    className="ncr-btn is-danger"
                    onClick={() =>
                      setPendingFiles((prev) =>
                        prev.filter((_, i) => i !== idx),
                      )
                    }
                  >
                    제거
                  </button>
                </li>
              ))}
              {attachments.length === 0 && pendingFiles.length === 0 ? (
                <li className="is-empty">등록된 첨부파일이 없습니다.</li>
              ) : null}
            </ul>
          </section>

          {isDetail ? (
            <div className="quality-form__actions">{renderDetailActions()}</div>
          ) : (
            renderEditActions()
          )}

          {editId != null ? (
            <ApprovalRequestModal
              open={approvalOpen}
              titleDefault={`${title || "외부심사"} 결재`}
              targetType={APPROVAL_TARGET_EXTERNAL_AUDIT}
              targetId={editId}
              onClose={() => setApprovalOpen(false)}
              onSubmitted={() => {
                setApprovalOpen(false);
                setApprovalMessage(
                  "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                );
                if (editId != null) {
                  void fetchExternalAudit(editId).then(applyDetail);
                }
                void loadList(searchKeyword);
              }}
            />
          ) : null}
        </form>
        {renderGuestModals()}
      </main>
    );
  }

  return (
    <main className="quality-shell sg-panel">
      <div className="quality-shell__toolbar">
        <div>
          <h2>{contractId != null ? "계약 외부심사" : "외부심사"}</h2>
          <p>
            {guestMode
              ? "게스트 열람 모드 · 회사 문서를 읽기 전용으로 조회합니다."
              : "인증·고객사 외부심사 기록, 심사관 정보, 지적/시정조치를 한 문서로 관리합니다."}
          </p>
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="search"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            placeholder="문서번호·제목·기관·내용 검색"
            style={{
              height: 34,
              padding: "0 12px",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              minWidth: 200,
              fontSize: 13,
            }}
          />
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-secondary"
              onClick={() => void openGuestManage()}
            >
              게스트 관리
            </button>
          ) : null}
          {!guestMode ? (
            <button
              type="button"
              className="ncr-btn is-primary"
              onClick={openCreate}
            >
              등록
            </button>
          ) : null}
        </div>
      </div>

      {error ? <p className="quality-error">{error}</p> : null}

      <div className="quality-table-wrap">
        <table className="quality-table">
          <thead>
            <tr>
              <th style={{ width: 130 }}>문서번호</th>
              <th>제목</th>
              <th style={{ width: 100 }}>유형</th>
              <th style={{ width: 140 }}>기관</th>
              <th style={{ width: 96 }}>심사일</th>
              <th style={{ width: 150 }}>지적요약</th>
              <th style={{ width: 72 }}>상태</th>
              <th style={{ width: 72 }}>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8}>불러오는 중…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  등록된 외부심사가 없습니다. 상단에서 등록해 주세요.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                return (
                  <tr
                    key={row.reportId}
                    onClick={() => void openDetail(row)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{row.reportNo || "-"}</td>
                    <td>{row.title}</td>
                    <td>{auditTypeLabel(row.auditType)}</td>
                    <td>{row.auditOrg || "-"}</td>
                    <td>{formatDate(row.auditDate)}</td>
                    <td onClick={(e: MouseEvent) => e.stopPropagation()}>
                      <FindingSummaryChips
                        findings={parseFindings(row.findingsJson)}
                      />
                    </td>
                    <td>
                      <span
                        className={`quality-badge is-${withApprovalPendingClass(
                          isApproved(row.status) ? "approved" : "draft",
                          row.approvalInProgress,
                        )}`}
                      >
                        {withApprovalPendingLabel(
                          isApproved(row.status) ? "승인" : "작성중",
                          row.approvalInProgress,
                        )}
                      </span>
                    </td>
                    <td>
                      {!guestMode &&
                      !isApproved(row.status) &&
                      !isApprovalInProgress(row) ? (
                        <button
                          type="button"
                          className="ncr-btn is-danger"
                          onClick={(e) => void handleDeleteFromList(row, e)}
                        >
                          삭제
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {renderGuestModals()}
    </main>
  );
}

export default ExternalAuditPanel;
